(() => {
  if (customElements.get('stack-lineup')) return;
  customElements.define('stack-lineup', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-lineup-slide]')];
      this.tabs = [...this.querySelectorAll('[data-lineup-tab]')];
      if (!this.slides.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.current = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.motion = this.querySelector('[data-lineup-motion]');
      this.status = this.querySelector('[data-lineup-status]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.reduced.matches;
      this.visible = false;
      this.busy = false;
      this.queued = null;
      this.animations = [];
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index), options);
        tab.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.tabs.length - 1;
          if (target === undefined) return;
          event.preventDefault();
          this.tabs[target].focus({ preventScroll: true });
          this.select(target);
        }, options);
      });
      this.motion.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncMedia();
      }, options);
      this.reduced.addEventListener('change', () => {
        this.userPaused = this.reduced.matches;
        this.animations.forEach(animation => animation.finish());
        this.syncMedia();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncMedia(), options);
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.select(index);
      }, options);
      let touchStart;
      this.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' && !event.target.closest('a,button')) touchStart = { x: event.clientX, y: event.clientY };
      }, options);
      this.addEventListener('pointerup', event => {
        if (!touchStart) return;
        const dx = event.clientX - touchStart.x;
        const dy = event.clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) this.select((this.current + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length);
      }, options);
      this.addEventListener('pointercancel', () => { touchStart = null; }, options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.syncMedia();
      }, { threshold: 0.05 });
      this.observer.observe(this);
      this.syncMedia();
    }
    syncMedia() {
      const active = this.slides[this.current]?.querySelector('video');
      this.querySelectorAll('video').forEach(video => {
        if (video === active && this.visible && !document.hidden && !this.userPaused) {
          video.muted = true;
          video.play().catch(() => {});
        } else video.pause();
      });
      this.motion.hidden = !active;
      this.motion.textContent = this.userPaused ? this.motion.dataset.play : this.motion.dataset.pause;
      this.motion.setAttribute('aria-pressed', String(this.userPaused));
    }
    async select(index) {
      if (this.busy) { this.queued = index; return; }
      if (index === this.current) return;
      this.busy = true;
      const previous = this.slides[this.current];
      const incoming = this.slides[index];
      const direction = index > this.current ? 1 : -1;
      incoming.classList.add('is-incoming');
      const video = incoming.querySelector('video');
      if (video && this.visible && !this.userPaused && !document.hidden) {
        video.muted = true;
        video.play().catch(() => {});
      }
      if (!this.reduced.matches && incoming.animate) {
        const closed = direction > 0 ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)';
        const settings = { duration: 760, easing: 'cubic-bezier(.22,.8,.22,1)', fill: 'both' };
        this.animations = [
          incoming.animate([{ clipPath: closed, transform: `translateX(${direction * 5}%)` }, { clipPath: 'inset(0 0 0 0)', transform: 'translateX(0)' }], settings),
          previous.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-direction * 3}%)` }], settings)
        ];
        await Promise.all(this.animations.map(animation => animation.finished.catch(() => {})));
      }
      if (!this.isConnected) return;
      this.current = index;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.classList.remove('is-incoming');
        slide.inert = i !== index;
        slide.setAttribute('aria-hidden', String(i !== index));
      });
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.animations.forEach(animation => animation.cancel());
      this.animations = [];
      this.status.textContent = incoming.dataset.announcement;
      this.syncMedia();
      this.busy = false;
      const next = this.queued;
      this.queued = null;
      if (next !== null && next !== this.current) this.select(next);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.animations?.forEach(animation => animation.cancel());
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
})();
